// src/ui/screens/chats/widgets/ConversationTile.jsx

import React from 'react';

const COLORS = {
  orange: '#FF8C08',
  greyTick: '#9E9E9E',
  blueTick: '#34B7F1',
};

// ── UserAvatar Component ──────────────────────────────────────────────────────

const UserAvatar = ({ size, imageUrl, name }) => {
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className="w-full h-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      className="flex items-center justify-center bg-filatom-primary text-white font-bold"
      style={{ width: size, height: size }}
    >
      {getInitials(name)}
    </div>
  );
};

// ── Selection Circle ─────────────────────────────────────────────────────────

const SelectionCircle = ({ isSelected }) => {
  return (
    <div
      className={`w-5.5 h-5.5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
        isSelected
          ? 'bg-filatom-primary border-filatom-primary'
          : 'border-white/55 bg-transparent'
      }`}
    >
      {isSelected && (
        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      )}
    </div>
  );
};

// ── Tick Icon ──────────────────────────────────────────────────────────────

const TickIcon = ({ status }) => {
  switch (status) {
    case 'sending':
      return (
        <svg className="w-3.5 h-3.5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth={2} />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2" />
        </svg>
      );
    case 'sent':
      return (
        <svg className="w-3.5 h-3.5" fill="none" stroke={COLORS.greyTick} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'delivered':
      return (
        <svg className="w-3.5 h-3.5" fill="none" stroke={COLORS.greyTick} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M7 15l2 2 4-4" />
        </svg>
      );
    case 'read':
      return (
        <svg className="w-3.5 h-3.5" fill="none" stroke={COLORS.blueTick} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M7 15l2 2 4-4" />
        </svg>
      );
    case 'failed':
      return (
        <svg className="w-3.5 h-3.5 text-red-500/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth={2} />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9l-6 6m0-6l6 6" />
        </svg>
      );
    default:
      return null;
  }
};

// ── ConversationTile ─────────────────────────────────────────────────────────

export const ConversationTile = ({
  name,
  lastMessage,
  time,
  avatarUrl = null,
  unreadCount = 0,
  isOnline = false,
  isPinned = false,
  isMuted = false,
  isBlocked = false,
  lastMessageStatus = null,
  iLastMessageMine = false,
  onTap = null,
  onLongPress = null,
  isSelectionMode = false,
  isSelected = false,
  onSelectionToggle = null,
}) => {
  const handlePress = () => {
    if (isSelectionMode && onSelectionToggle) {
      onSelectionToggle();
    } else if (onTap) {
      onTap();
    }
  };

  const handleLongPress = () => {
    if (onLongPress) {
      onLongPress();
    }
  };

  const displayLastMessage = isBlocked ? '🚫 You blocked this user' : lastMessage;

  const hasUnread = unreadCount > 0 && !isMuted && !iLastMessageMine;

  return (
    <div
      className={`transition-colors duration-200 ${
        isSelected ? `bg-[${COLORS.orange}]/10` : 'bg-transparent'
      }`}
    >
      <button
        onClick={handlePress}
        onContextMenu={(e) => {
          e.preventDefault();
          if (onLongPress) onLongPress();
        }}
        className="w-full px-3 py-2.5 flex items-center gap-3"
      >
        {/* Selection circle */}
        <div
          className={`transition-all duration-200 overflow-hidden ${
            isSelectionMode ? 'w-7 opacity-100' : 'w-0 opacity-0'
          }`}
          style={{ height: 60 }}
        >
          <div className="flex items-center justify-center w-7">
            <SelectionCircle isSelected={isSelected} />
          </div>
        </div>

        {isSelectionMode && <div className="w-1.5" />}

        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div
            className={`rounded-full overflow-hidden w-15 h-15 ${
              isBlocked ? 'grayscale' : ''
            }`}
          >
            <UserAvatar
              size={60}
              imageUrl={avatarUrl || null}
              name={name}
            />
          </div>

          {/* Online dot */}
          {isOnline && !isSelectionMode && !isBlocked && (
            <div className="absolute bottom-0.5 right-0.5 w-3 h-3 rounded-full border-2 border-white bg-green-500" />
          )}

          {/* Pinned badge */}
          {isPinned && !isSelectionMode && (
            <div className="absolute bottom-0 -right-0.5 w-4.5 h-4.5 rounded-full border border-white/10 flex items-center justify-center" style={{ backgroundColor: '#23262C' }}>
              <svg className="w-2.5 h-2.5 text-filatom-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z" />
              </svg>
            </div>
          )}
        </div>

        {/* Name + message */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 min-w-0">
              <p
                className={`text-sm font-bold truncate ${
                  isBlocked ? 'text-white/40' : 'text-white'
                }`}
              >
                {name}
              </p>
              {isMuted && !isSelectionMode && (
                <svg className="w-3 h-3 text-white/35 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              )}
            </div>
            <span className={`text-xs flex-shrink-0 ${isSelected ? `text-[${COLORS.orange}]` : 'text-gray-400'}`}>
              {time}
            </span>
          </div>

          <div className="flex items-center gap-1 mt-1">
            {/* Tick icon */}
            {iLastMessageMine && lastMessageStatus && !isSelectionMode && !isBlocked && (
              <TickIcon status={lastMessageStatus} />
            )}

            <p
              className={`text-xs truncate flex-1 ${
                isBlocked
                  ? 'text-white/20 italic'
                  : hasUnread
                  ? 'text-white font-medium'
                  : 'text-gray-400'
              }`}
            >
              {displayLastMessage}
            </p>

            {/* Unread badge */}
            {hasUnread && !isSelectionMode && (
              <div
                className="ml-1.5 px-1.5 py-0.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: COLORS.orange }}
              >
                <span className="text-black text-[10px] font-bold">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              </div>
            )}

            {/* Muted unread indicator */}
            {unreadCount > 0 && !isSelectionMode && isMuted && !iLastMessageMine && (
              <div className="w-2 h-2 rounded-full bg-white/25 flex-shrink-0" />
            )}
          </div>
        </div>
      </button>

      {/* Divider */}
      <div className="ml-[78px] h-px bg-white/10" />
    </div>
  );
};

export default ConversationTile;